import { currentLocale, intlLocaleFor } from "./index";

/**
 * Numbers and clock times in the viewer's language and time zone, always
 * through `intlLocaleFor()` (never hand `bm` / `tet` to Intl). Absolute
 * times only — an operator needs "14:05", never "5 min ago".
 */

const cache = new Map<string, Intl.DateTimeFormat | Intl.NumberFormat>();

function dtf(key: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
	const id = `${currentLocale()}|d|${key}`;
	let f = cache.get(id) as Intl.DateTimeFormat | undefined;
	if (!f) {
		f = new Intl.DateTimeFormat(intlLocaleFor(currentLocale()), opts);
		cache.set(id, f);
	}
	return f;
}

const toDate = (v: string | Date): Date => (typeof v === "string" ? new Date(v) : v);

/** `14:05` (or the locale's own clock). */
export const formatClock = (v: string | Date): string =>
	dtf("clock", { hour: "numeric", minute: "2-digit" }).format(toDate(v));

/** `2:05:37 PM` — an audit trail's exact instant (the full stamp with its zone goes in the tooltip). */
export const formatClockExact = (v: string | Date): string =>
	dtf("clockExact", { hour: "numeric", minute: "2-digit", second: "2-digit" }).format(toDate(v));

/** `Wed 1 Oct` — the day chip. */
export const formatDay = (v: string | Date): string =>
	dtf("day", { weekday: "short", day: "numeric", month: "short" }).format(toDate(v));

/** `1 Oct, 2:05 PM` — dense lists (matches GMS `formatShort`). */
export const formatShort = (v: string | Date): string =>
	dtf("short", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }).format(toDate(v));

/** Full stamp with zone, for tooltips. */
export const formatDateTime = (v: string | Date): string =>
	dtf("full", {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
		timeZoneName: "short",
	}).format(toDate(v));

/**
 * The full stamp in ANOTHER zone — the games' (`Asia/Kuala_Lumpur`), for the
 * tooltip beside a viewer-local time. Not cached per zone beyond the locale:
 * a handful of zones exist.
 */
export function formatDateTimeIn(v: string | Date, timeZone: string): string {
	try {
		return dtf(`full|${timeZone}`, {
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "numeric",
			minute: "2-digit",
			timeZoneName: "short",
			timeZone,
		}).format(toDate(v));
	} catch {
		return formatDateTime(v);
	}
}

/** The viewer's zone, short (`GMT+8`, `ICT`) — named beside the clock. */
export const viewerZoneName = (v: Date = new Date()): string =>
	dtf("zoneName", { timeZoneName: "short" }).formatToParts(v).find((p) => p.type === "timeZoneName")?.value ?? "";

/** A plain integer in the viewer's digits (`1,234`). */
export function formatInteger(n: number): string {
	const id = `${currentLocale()}|n|int`;
	let f = cache.get(id) as Intl.NumberFormat | undefined;
	if (!f) {
		f = new Intl.NumberFormat(intlLocaleFor(currentLocale()), { maximumFractionDigits: 0 });
		cache.set(id, f);
	}
	return f.format(n);
}

/**
 * `Fri 5 Dec` for a CALENDAR day (`YYYY-MM-DD`, e.g. a games day from
 * `GET /meets/days`). A date, not an instant: formatted at UTC noon in UTC so
 * no viewer's zone can move it to the day before or after.
 */
export const formatCalendarDay = (day: string): string =>
	dtf("calendarDay", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" }).format(new Date(`${day}T12:00:00Z`));
